import { MK_CSS, MarketingNav, MarketingFooter, useMkNav, CheckCircleIcon } from "./MarketingChrome";

const CSS = `
.fp-hero h1 { margin-bottom:24px; font-size:clamp(40px,6vw,64px); }
.fp-row-copy p.eyebrow { margin-bottom:16px; font-weight:600; font-size:16px; color:var(--olive); }
.fp-row-copy h2 { margin-bottom:24px; font-size:clamp(30px,4.5vw,44px); }
.fp-row-copy > p { font-size:18px; line-height:1.5; }
.fp-row-copy .mk-check { margin-top:32px; }
.fp-row-img { width:100%; aspect-ratio:4/3; }
.fp-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:32px; }
.fp-cell { display:flex; flex-direction:column; background:var(--panel); border-radius:8px; padding:32px; }
.fp-cell .tag { margin-bottom:8px; font-size:14px; font-weight:600; }
.fp-cell h3 { margin-bottom:8px; font-size:26px; }
.fp-cell p { font-size:16px; line-height:1.5; }
@media (max-width:900px){ .fp-grid { grid-template-columns:1fr; } }
`;

const ROWS = [
  {
    flip: false, eyebrow: "Capture", title: "Snap it, and the data files itself",
    body: "Photograph any invoice on the iPad and the AI reads supplier, date, totals, and every line item. Review, correct, and file in seconds — no keying, no filing cabinet.",
    checks: ["Line-item extraction from photos or PDFs", "Auto-matched to the right supplier", "Flags totals that don’t add up"],
    img: "Invoice capture",
  },
  {
    flip: true, eyebrow: "Track", title: "Every price change, every supplier, over time",
    body: "Bill Lens keeps a running history of what you pay. Spot creeping costs before they eat your margin and hold suppliers to the price they quoted.",
    checks: ["Full price history per product", "Alerts when a price jumps", "Compare suppliers side by side"],
    img: "Price history",
  },
  {
    flip: false, eyebrow: "Cost", title: "Plate costs that stay accurate on their own",
    body: "Build recipes once and Bill Lens keeps the cost live as supplier prices move. Know your margin on every dish before it hits the pass.",
    checks: ["Live plate and menu costing", "Margin targets and warnings", "Yield and portion adjustments"],
    img: "Menu costing",
  },
];

const REST = [
  { tag: "Export", h: "Clean accounting exports", p: "One-click files for Xero, QuickBooks, or your accountant’s inbox." },
  { tag: "Reports", h: "Spend reports on demand", p: "Pull spend by supplier, category, or site in a couple of clicks." },
  { tag: "Roles", h: "Access for the whole team", p: "Managers, chefs, and finance each get the view that fits their job." },
  { tag: "Approvals", h: "Approve before you pay", p: "Route invoices for sign-off and keep a clean audit trail." },
  { tag: "Search", h: "Find any invoice fast", p: "Full-text search across every document you’ve ever captured." },
  { tag: "Storage", h: "Nothing gets lost", p: "Every invoice stored securely and backed up, forever." },
];

export default function FeaturesPage() {
  const nav = useMkNav();

  return (
    <div className="mk">
      <style>{MK_CSS}</style>
      <style>{CSS}</style>

      <MarketingNav active="features" />

      {/* HERO */}
      <section className="mk-sec dark fp-hero">
        <div className="mk-narrow">
          <p className="mk-eyebrow head" style={{ color: "var(--lolive)" }}>Features</p>
          <h1>Everything you need to control food costs</h1>
          <p>From the moment an invoice lands to the day you close the books, Bill Lens keeps every number clean, current, and ready to act on.</p>
          <div className="mk-cta-row">
            <button className="mk-btn mk-btn-lg mk-btn-primary" onClick={nav.goSignup}>Start trial</button>
            <button className="mk-btn mk-btn-lg mk-btn-glass" onClick={nav.goPricing}>See pricing</button>
          </div>
        </div>
      </section>

      {/* FEATURE ROWS */}
      {ROWS.map((r) => (
        <section className="mk-sec" key={r.eyebrow}>
          <div className="mk-wrap mk-two">
            {r.flip && <div className="mk-img fp-row-img" style={{ order: 0 }}><span className="ph">{r.img}</span></div>}
            <div className="fp-row-copy">
              <p className="eyebrow">{r.eyebrow}</p>
              <h2>{r.title}</h2>
              <p>{r.body}</p>
              <ul className="mk-check">
                {r.checks.map((c) => <li key={c}><CheckCircleIcon /><span>{c}</span></li>)}
              </ul>
            </div>
            {!r.flip && <div className="mk-img fp-row-img"><span className="ph">{r.img}</span></div>}
          </div>
        </section>
      ))}

      {/* SECONDARY FEATURES */}
      <section className="mk-sec">
        <div className="mk-wrap">
          <div className="mk-center-head">
            <p className="mk-eyebrow head" style={{ color: "var(--olive)" }}>And the rest</p>
            <h2>The details that keep the house running</h2>
            <p>Everything else you’d expect from a tool your whole team actually uses.</p>
          </div>
          <div className="fp-grid">
            {REST.map((f) => (
              <div className="fp-cell" key={f.tag}>
                <p className="tag">{f.tag}</p>
                <h3>{f.h}</h3>
                <p>{f.p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mk-sec dark">
        <div className="mk-narrow">
          <h2>See it on your own invoices</h2>
          <p>Upload your first invoice and watch the data come clean. No setup fees, no training manual.</p>
          <div className="mk-cta-row">
            <button className="mk-btn mk-btn-lg mk-btn-primary" onClick={nav.goSignup}>Start trial</button>
            <button className="mk-btn mk-btn-lg mk-btn-glass" onClick={nav.goAbout}>Talk to us</button>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
